import React from 'react';
import { LayoutLogin } from 'components/Layout';
import LayoutAuthenticated from 'components/Layout/LayoutAuthenticated';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import * as Sentry from '@sentry/react';
import LoginPage from 'pages/LoginPage';
import RecoverPassword from 'pages/RecoverPassword';
import RecoverPasswordSuccess from 'pages/RecoverPasswordSuccess';
import ZonesPage from 'pages/ZonesPage';
import UsersAndContactsPage from 'pages/UsersAndContactsPage';
import MainPage from 'pages/MainPage';
import ErrorBoundary from 'components/ErrorBoundary';
import { tokenAtom } from 'atoms';
import PlacePage from 'pages/PlacePage';
import EventsPage from 'pages/EventsPage';
import EventDetailsPage from 'pages/EventDetailsPage';
import ServiceOrdersPage from 'pages/ServiceOrdersPage';
import ServiceOrderDetailsPage from 'pages/ServiceOrderDetailsPage';
import TimetablePage from 'pages/TimetablePage';
import RequirePermission from 'components/RequirePermission';
import ExternalAccessPage from 'pages/ExternalAccessPage';
import LoadAccounts from 'components/LoadAccounts';
import RedirectingPage from 'pages/RedirectingPage';
import ChangePasswordPage from 'pages/ChangePassword';
import CameraViewPage from 'pages/CameraViewPage';
import CamerasPage from 'pages/CamerasPage';
import SummaryPage from 'pages/SummaryPage';
import EventDetailsAccountPage from 'pages/EventDetailsAccountPage';
import ServiceOrderDetailsAccountPage from 'pages/ServiceOrderDetailsAccountPage';
import AlreadyLoggedPage from 'pages/AlreadyLoggedPage';
import AlreadyLoggedSendedPage from 'pages/AlreadyLoggedSendedPage';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function MySecurityRoutes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <SentryRoutes>
          <Route path="/external-access" element={<ExternalAccessPage />} />
          <Route path="/redirecting" element={<RedirectingPage />} />
          <Route
            element={
              <RequireEmptyToken>
                <LayoutLogin />
              </RequireEmptyToken>
            }
          >
            <Route path="/login" element={<LoginPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/recover-password" element={<RecoverPassword />} />
            <Route path="/recover-password/success" element={<RecoverPasswordSuccess />} />
            <Route path="/already-logged" element={<AlreadyLoggedPage />} />
            <Route path="/already-logged/sended" element={<AlreadyLoggedSendedPage />} />
          </Route>

          <Route
            element={
              <RequireAuth>
                <LoadAccounts>
                  <LayoutAuthenticated />
                </LoadAccounts>
              </RequireAuth>
            }
          >
            <Route path="/" element={<MainPage />} />
            <Route path="/place" element={<PlacePage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:eventType/:eventId" element={<EventDetailsPage />} />
            <Route
              path="/events/:eventType/:eventId/:accountId"
              element={<EventDetailsAccountPage />}
            />
            <Route
              path="/zones"
              element={
                <RequirePermission
                  validatePermission={(account) => !!account?.showZones}
                  fallbackOnInvalid={<Navigate to={`/`} replace />}
                >
                  <ZonesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/cameras/:camName"
              element={
                <RequirePermission
                  validatePermission={(account) => !!account?.hasPermissionToViewCameras}
                  fallbackOnInvalid={<Navigate to={`/`} replace />}
                >
                  <CameraViewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/contacts"
              element={
                <RequirePermission
                  validatePermission={(account) => !!account?.showContacts || !!account?.showUsers}
                  fallbackOnInvalid={<Navigate to={`/`} replace />}
                >
                  <UsersAndContactsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/service-orders"
              element={
                <RequirePermission
                  validatePermission={(account) => !account || account.hasPermissionToViewSO}
                  fallbackOnInvalid={<Navigate to={`/`} replace />}
                >
                  <ServiceOrdersPage />
                </RequirePermission>
              }
            />
            <Route path="/service-orders/:serviceOrderId" element={<ServiceOrderDetailsPage />} />
            <Route
              path="/service-orders/:serviceOrderId/:accountId"
              element={<ServiceOrderDetailsAccountPage />}
            />
            <Route path="/summary" element={<SummaryPage />} />
            <Route
              path="/timetable"
              element={
                <RequirePermission
                  validatePermission={(account) => !!account?.showTimeTable}
                  fallbackOnInvalid={<Navigate to={`/`} replace />}
                >
                  <TimetablePage />
                </RequirePermission>
              }
            />
            <Route
              path="/cameras"
              element={
                <RequirePermission
                  validatePermission={(account) => !!account?.hasPermissionToViewCameras}
                  fallbackOnInvalid={<Navigate to={`/`} replace />}
                >
                  <CamerasPage />
                </RequirePermission>
              }
            />
            <Route path="*" element={<MainPage />} />
          </Route>
          <Route
            element={
              <RequireAuth>
                <LayoutLogin />
              </RequireAuth>
            }
          >
            <Route path="/change-login-password" element={<ChangePasswordPage />} />
          </Route>
        </SentryRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

function RequireEmptyToken({ children }: { children: JSX.Element }) {
  const token = useRecoilValue(tokenAtom);

  if (token) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const token = useRecoilValue(tokenAtom);
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

export default MySecurityRoutes;
